/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useEffect, useState } from "react";
import { buildImageUrl } from "../utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";
import { getDeliveryByCity } from "../../utils/storeApi";
export default function OrderDetailsPage({ order, theme, onClose, onEdit, onDelete }) {
  const [shipping, setShipping] = useState(0);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;

  const customerName =
    order.customerName ||
    JSON.parse(localStorage.getItem("storeCustomer") || "{}")?.name ||
    "-";

  const paymentStatusText = Number(order.isPaid) === 1 ? "مدفوع" : "غير مدفوع";

  const validDetails = (order.details || []).map(d => ({
    itemId: d.itemId || d.ItemId,
    variantId: d.variantId || d.VariantId,
    itemName: d.itemName || d.ItemName,
    variantName: d.variantName || d.VariantName,
    colorName: d.colorName || d.ColorName || d.color,
    size: d.size || d.Size,
    quantity: Number(d.quantity || d.Quantity || 0),
    price: Number(d.price || d.Price || 0),
    lineTotal: Number(d.lineTotal || d.LineTotal || 0),
    imageUrl: d.imageUrl || d.ImageUrl
  })).filter(d => d.quantity > 0);

  const subTotal = validDetails.reduce((sum, item) => sum + (Number(item.lineTotal) || (Number(item.price) * Number(item.quantity)) || 0), 0);
  const discount = Number(order.discountAmount || 0);
  const finalTotal = Math.max(subTotal - discount + Number(shipping || 0), 0);

  useEffect(() => {
    let alive = true;
    if (!order) return;
    const tenantId = Number(order.tenantID || order.tenantId || 0);
    const cityId = Number(order.cityId || 0);
    if (!tenantId || !cityId) {
      setShipping(0);
      return;
    }
    getDeliveryByCity(tenantId, cityId).then((res) => {
      if (!alive) return;
      setShipping(Number(res?.deliveryCompanyFees || res?.deliveryFees || res?.fee || 0));
    }).catch(() => {
      if (alive) setShipping(0);
    });
    return () => { alive = false; };
  }, [order]);

  if (!order) return null;
  const isNewOrder = Number(order.statusId) === 1;

  return (
    <div style={{
      ...overlay,
      alignItems: "stretch",
      padding: 0,
      background: "rgba(0,0,0,0.6)"
    }}>
      <div style={{
        ...modal,
        width: "100vw",
        maxWidth: "100vw",
        borderRadius: 0,
        padding: 18,
        maxHeight: "100vh",
        height: "100vh",
        margin: 0
      }}>
        <div style={header}>
          <h3>تفاصيل الطلب #{order.orderNo || order.orderId?.slice(0, 6)}</h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={{
          ...infoBox,
          display: isMobile ? "block" : "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(220px, 1fr))",
          columnGap: "18px",
          rowGap: "8px",
          textAlign: "right"
        }}>
          <div>العميل: {customerName}</div>
          <div>الهاتف: {order.customerPhone1 || "-"}</div>
          <div>المحافظة: {order.city || "-"}</div>
          <div>العنوان: {order.address || "-"}</div>
          <div>طريقة الدفع: {order.paymentMethodName || "-"}</div>
          <div>حالة الدفع: {paymentStatusText}</div>
          <div>حالة الطلب: {order.status || "-"}</div>
          <div>التاريخ: {order.orderDate || "-"}</div>
          {order.coupon && <div>الكوبون: {order.coupon}</div>}
        </div>

        <div style={content} className="cart-scrollable">
          {validDetails.map((item, i) => (
            <div key={`od-${item.id}-${i}`} style={itemCard}>
              <img
                src={buildImageUrl(item.imageUrl)}
                onError={e => e.target.src = "https://placehold.co/70x70?text=No+Image"}
                style={img}
              />

              <div style={{ flex: 1 }}>
                <b>{item.itemName || "منتج"}</b>
                {item.variantName && <div style={meta}>النوع: {item.variantName}</div>}

                {(item.colorName || item.color) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                    <span style={meta}>اللون:</span>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: item.colorName || item.color,
                        border: "1px solid #ccc",
                        display: "inline-block"
                      }}
                    />
                  </div>
                )}

                {item.size && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                    <span style={meta}>المقاس:</span>
                    <span
                      style={{
                        minWidth: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: "1px solid #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#444",
                        background: "#fff"
                      }}
                    >
                      {item.size}
                    </span>
                  </div>
                )}
                <div style={meta}>الكمية: {item.quantity}</div>
                <div style={{ color: theme.primary, fontWeight: 700 }}>
                  {formatNumber(item.lineTotal || item.price * item.quantity)} IRQ
                </div>
              </div>
            </div>
          ))}

          {/* ملخص الحساب */}
          <div style={{
            marginTop: 20,
            padding: 16,
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #eee",
            fontSize: 13,
            color: "#444",
            lineHeight: 2
          }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>المجموع الفرعي:</span>
              <span>{formatNumber(subTotal)} IRQ</span>
            </div>
            {discount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", color: "#e5533d" }}>
                <span>الخصم:</span>
                <span>-{formatNumber(discount)} IRQ</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>أجور التوصيل:</span>
              <span>{Number(shipping) <= 0 ? "مجاني" : `${formatNumber(shipping)} IRQ`}</span>
            </div>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 800,
              fontSize: 16,
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid #eee",
              color: theme.primary
            }}>
              <span>المجموع الكلي:</span>
              <span>{formatNumber(finalTotal)} IRQ</span>
            </div>
          </div>
        </div>

        {isNewOrder && (
          <div style={footer}>
            <button onClick={() => onEdit(order)} style={btnEdit}>تعديل الطلب</button>
            <button onClick={() => onDelete(order)} style={btnDelete}>حذف الطلب</button>
          </div>
        )}
      </div>
    </div>
  );
}
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  zIndex: 26000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const modal = {
  width: "100%",
  maxWidth: 520,
  background: "#fff",
  borderRadius: 22,
  padding: 22,
  display: "flex",
  flexDirection: "column",
  maxHeight: "88vh",
  boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
};

const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 10
};

const closeBtn = {
  background: "none",
  border: "none",
  fontSize: 26,
  cursor: "pointer",
  color: "#444"
};

const infoBox = {
  background: "#f7f9fc",
  padding: "14px 16px",
  borderRadius: 14,
  fontSize: 13,
  color: "#444",
  lineHeight: 1.8,
  marginBottom: 14,
  textAlign: "center"
};


const content = {
  flex: 1,
  overflowY: "auto",
  paddingRight: 4
};

const itemCard = {
  display: "flex",
  gap: 14,
  padding: 12,
  background: "#fafafa",
  borderRadius: 14,
  marginBottom: 10,
  border: "1px solid #eee"
};

const img = {
  width: 72,
  height: 72,
  objectFit: "contain",
  background: "#fff",
  borderRadius: 12,
  padding: 6
};

const meta = {
  fontSize: 12,
  color: "#777",
  marginTop: 2
};

const footer = {
  display: "flex",
  gap: 14,
  marginTop: 16
};


const btnEdit = {
  flex: 1,
  background: "#2c7be5",
  color: "#fff",
  border: "none",
  borderRadius: 14,
  padding: "14px 0",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 15,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center"
};

const btnDelete = {
  flex: 1,
  background: "#e5533d",
  color: "#fff",
  border: "none",
  borderRadius: 14,
  padding: "14px 0",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 15,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center"
};

